'use client';

import { Fragment, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { exportToCSV, exportToPDF, prepareExportColumns } from './export-utils';
import { Icon } from './icon';

export interface Column<T> {
  key: string;
  header: string;
  accessor?: keyof T;
  render?: (item: T) => ReactNode;
  sortable?: boolean;
  type?: 'actions';
  width?: number;
  resizable?: boolean;
  reorderable?: boolean;
  hideable?: boolean;
  hidden?: boolean;
  align?: 'left' | 'center' | 'right';
  editable?: boolean;
  validate?: (value: string, item: T) => string | null | Promise<string | null>;
  placeholder?: string;
  exportValue?: (item: T) => string | number;
}

export interface BulkAction {
  label: string;
  variant?: 'default' | 'danger';
  onClick: (items: any[]) => void;
}

export interface PaginationMeta {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface EmptyStateConfig {
  icon?: ReactNode;
  title?: string;
  description?: string;
  action?: { label: string; onClick: () => void };
}

export interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  meta?: PaginationMeta;
  onPageChange?: (page: number) => void;
  onSort?: (column: string, direction: 'asc' | 'desc') => void;
  resizable?: boolean;
  reorderable?: boolean;
  showColumnToggle?: boolean;
  storageKey?: string;
  selectable?: boolean;
  onRowSelect?: (ids: string[]) => void;
  bulkActions?: BulkAction[];
  inlineEdit?: boolean;
  onCellEdit?: (item: T, columnKey: string, value: string) => void | Promise<void>;
  loading?: boolean;
  error?: string;
  emptyMessage?: string;
  emptyState?: EmptyStateConfig;
  expandedRowRender?: (item: T) => ReactNode;
  expandedRowId?: string | null;
  onRowExpand?: (id: string | null) => void;
  exportable?: boolean;
  exportFileName?: string;
  exportTitle?: string;
  exportSubtitle?: string;
  onExportAll?: () => Promise<T[]>;
  onDragStart?: (id: string, e: React.DragEvent) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: (id: string, e: React.DragEvent) => void;
}

type Layout = {
  widths: Record<string, number>;
  order: string[];
  hidden: string[];
};

export function DataTable<T extends { id: string }>({
  data,
  columns,
  meta,
  onPageChange,
  onSort,
  resizable = true,
  reorderable = true,
  showColumnToggle = true,
  storageKey,
  selectable = false,
  onRowSelect,
  bulkActions = [],
  inlineEdit = false,
  onCellEdit,
  loading = false,
  error,
  emptyMessage = 'No data found.',
  emptyState,
  expandedRowRender,
  expandedRowId,
  onRowExpand,
  exportable = true,
  exportFileName = 'export',
  exportTitle,
  exportSubtitle,
  onExportAll,
  onDragStart,
  onDragOver,
  onDrop,
}: DataTableProps<T>) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const onRowSelectRef = useRef(onRowSelect);
  onRowSelectRef.current = onRowSelect;

  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const [widths, setWidths] = useState<Record<string, number>>(() =>
    Object.fromEntries(columns.map(c => [c.key, c.width ?? 180]))
  );
  const [order, setOrder] = useState<string[]>(() => columns.map(c => c.key));
  const [hidden, setHidden] = useState<string[]>(() =>
    columns.filter(c => c.hidden).map(c => c.key)
  );
  const [layoutLoaded, setLayoutLoaded] = useState(!storageKey);
  const [showColumns, setShowColumns] = useState(false);

  const draggedColumn = useRef<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);

  const resizeState = useRef<{ key: string; startX: number; startWidth: number } | null>(null);

  const [editing, setEditing] = useState<{ rowId: string; key: string } | null>(null);
  const [draft, setDraft] = useState('');
  const [editError, setEditError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const columnKeys = columns.map(c => c.key).join('|');

  useEffect(() => {
    if (!storageKey) return;
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const saved = JSON.parse(raw) as Layout;
        if (saved.order?.every(k => columnKeys.split('|').includes(k))) {
          setOrder(saved.order);
          setWidths(prev => ({ ...prev, ...saved.widths }));
          setHidden(saved.hidden ?? []);
        }
      }
    } catch {}
    setLayoutLoaded(true);
  }, [storageKey, columnKeys]);

  useEffect(() => {
    if (!storageKey || !layoutLoaded) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify({ widths, order, hidden }));
    } catch {}
  }, [widths, order, hidden, storageKey, layoutLoaded]);

  useEffect(() => {
    onRowSelectRef.current?.(selectedIds);
  }, [selectedIds]);

  useEffect(() => {
    setOrder(prev => [
      ...prev.filter(k => columns.some(c => c.key === k)),
      ...columns.filter(c => !prev.includes(c.key)).map(c => c.key),
    ]);
  }, [columnKeys, columns]);

  const visibleColumns = useMemo(() => {
    return order.flatMap(key => {
      const col = columns.find(c => c.key === key);
      return col && !hidden.includes(key) ? [col] : [];
    });
  }, [order, columns, hidden]);

  const sortedData = useMemo(() => {
    if (onSort || !sortColumn) return data;
    const col = columns.find(c => c.key === sortColumn);
    if (!col) return data;
    const getValue = (item: T) => item[(col.accessor ?? col.key) as keyof T];
    return [...data].sort((a, b) => {
      const av = getValue(a);
      const bv = getValue(b);
      let result = 0;
      if (typeof av === 'number' && typeof bv === 'number') result = av - bv;
      else result = String(av ?? '').localeCompare(String(bv ?? ''), undefined, { numeric: true });
      return sortDirection === 'asc' ? result : -result;
    });
  }, [data, columns, sortColumn, sortDirection, onSort]);

  const selectedItems = data.filter(item => selectedIds.includes(item.id));

  function handleSort(col: Column<T>) {
    if (!col.sortable) return;
    const nextDirection = sortColumn === col.key && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortColumn(col.key);
    setSortDirection(nextDirection);
    onSort?.(col.key, nextDirection);
  }

  function toggleHidden(key: string) {
    setHidden(prev => {
      if (prev.includes(key)) return prev.filter(k => k !== key);
      if (visibleColumns.length <= 1) return prev;
      return [...prev, key];
    });
  }

  function resetLayout() {
    setOrder(columns.map(c => c.key));
    setWidths(Object.fromEntries(columns.map(c => [c.key, c.width ?? 180])));
    setHidden(columns.filter(c => c.hidden).map(c => c.key));
  }

  function startResize(e: React.PointerEvent, col: Column<T>) {
    e.preventDefault();
    e.stopPropagation();
    resizeState.current = { key: col.key, startX: e.clientX, startWidth: widths[col.key] ?? 180 };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }

  function moveResize(e: React.PointerEvent) {
    const state = resizeState.current;
    if (!state) return;
    const next = Math.max(60, state.startWidth + (e.clientX - state.startX));
    setWidths(prev => ({ ...prev, [state.key]: next }));
  }

  function endResize(e: React.PointerEvent) {
    resizeState.current = null;
    try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch {}
  }

  function reorderColumns(fromKey: string, toKey: string) {
    if (fromKey === toKey) return;
    setOrder(prev => {
      const next = [...prev];
      const fromIndex = next.indexOf(fromKey);
      const toIndex = next.indexOf(toKey);
      if (fromIndex < 0 || toIndex < 0) return prev;
      next.splice(fromIndex, 1);
      next.splice(toIndex, 0, fromKey);
      return next;
    });
  }

  function startEdit(item: T, col: Column<T>) {
    const raw = item[(col.accessor ?? col.key) as keyof T];
    setDraft(typeof raw === 'object' ? '' : String(raw ?? ''));
    setEditError(null);
    setEditing({ rowId: item.id, key: col.key });
  }

  async function commitEdit() {
    if (!editing || !onCellEdit) return;
    const col = columns.find(c => c.key === editing.key);
    const item = data.find(r => r.id === editing.rowId);
    if (!col || !item) return;

    setEditError(null);
    try {
      if (col.validate) {
        const message = await col.validate(draft, item);
        if (message) { setEditError(message); return; }
      }
      setSaving(true);
      await onCellEdit(item, col.key, draft);
      setEditing(null);
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleExportCSV() {
    const itemsToExport = onExportAll ? await onExportAll() : (selectedIds.length > 0 ? selectedItems : data);
    const exportCols = prepareExportColumns(columns);
    exportToCSV(itemsToExport, exportCols, exportFileName);
  }

  async function handleExportPDF() {
    const itemsToExport = onExportAll ? await onExportAll() : (selectedIds.length > 0 ? selectedItems : data);
    const exportCols = prepareExportColumns(columns);
    exportToPDF(itemsToExport, exportCols, {
      title: exportTitle,
      subtitle: exportSubtitle,
      filename: exportFileName,
    });
  }

  if (error) {
    return <div className="p-8 text-center text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200">{error}</div>;
  }

  if (loading && data.length === 0) {
    return (
      <div className="p-12 text-center text-gray-500">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
        Loading data...
      </div>
    );
  }

  const tableWidth = visibleColumns.reduce((sum, c) => sum + (widths[c.key] ?? 180), 0) + (selectable ? 48 : 0);

  return (
    <div className="w-full">
      <div className="mb-3 flex flex-wrap items-center justify-end gap-2">
        {selectedIds.length > 0 && (
          <div className="text-xs font-medium text-blue-600">
            {selectedIds.length} selected
          </div>
        )}

        <div className="flex items-center gap-2">
          {showColumnToggle && (
            <div className="relative mr-2">
              <button
                onClick={() => setShowColumns(v => !v)}
                className="px-3 py-1.5 text-xs font-medium border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Columns ▾
              </button>

              {showColumns && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowColumns(false)} />
                  <div className="absolute right-0 top-full mt-2 z-20 w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 max-h-72 overflow-y-auto">
                    <p className="text-xs font-semibold text-gray-500 mb-2">VISIBLE COLUMNS</p>
                    {columns.map(col => (
                      <label key={col.key} className="flex items-center gap-2 py-1 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!hidden.includes(col.key)}
                          disabled={col.hideable === false}
                          onChange={() => toggleHidden(col.key)}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <span className="text-gray-700 dark:text-gray-200">{col.header}</span>
                      </label>
                    ))}
                    <button onClick={resetLayout} className="mt-3 pt-2 w-full text-left text-xs text-blue-600 border-t border-gray-200 dark:border-gray-700 flex items-center gap-1">
                      <Icon name="RotateCcw" size={12} /> Reset layout
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {exportable && (
            <div className="flex gap-2">
              <button
                onClick={handleExportCSV}
                className="px-3 py-1.5 text-xs font-medium bg-green-600 text-white hover:bg-green-700 rounded-md flex items-center gap-1 transition-colors"
                title="Export to CSV"
              >
                <Icon name="Download" size={14} />
                CSV{selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}
              </button>
              <button
                onClick={handleExportPDF}
                className="px-3 py-1.5 text-xs font-medium bg-red-600 text-white hover:bg-red-700 rounded-md flex items-center gap-1 transition-colors"
                title="Export to PDF"
              >
                <Icon name="FileText" size={14} />
                PDF{selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}
              </button>
            </div>
          )}
        </div>
      </div>

      {selectable && selectedIds.length > 0 && bulkActions.length > 0 && (
        <div className="mb-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 rounded-lg flex flex-wrap items-center justify-between gap-2">
          <span className="text-sm text-blue-700 font-medium">{selectedIds.length} item(s) selected</span>
          <div className="flex gap-2">
            {bulkActions.map((action, idx) => (
              <button
                key={idx}
                onClick={() => action.onClick(selectedItems)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md text-white ${action.variant === 'danger' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}
              >
                {action.label}
              </button>
            ))}
            <button onClick={() => setSelectedIds([])} className="px-3 py-1.5 text-xs rounded-md border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800">Clear</button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="border-collapse text-left" style={{ tableLayout: 'fixed', width: tableWidth }}>
          <colgroup>
            {selectable && <col style={{ width: 48 }} />}
            {visibleColumns.map(col => <col key={col.key} style={{ width: widths[col.key] ?? 180 }} />)}
          </colgroup>

          <thead className="bg-gray-50 dark:bg-gray-800/80 border-b border-gray-200 dark:border-gray-700">
            <tr>
              {selectable && (
                <th className="px-3 py-3">
                  <input
                    type="checkbox"
                    checked={data.length > 0 && selectedIds.length === data.length}
                    onChange={e => setSelectedIds(e.target.checked ? data.map(r => r.id) : [])}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                </th>
              )}

              {visibleColumns.map(col => {
                const canResize = resizable && col.resizable !== false && col.type !== 'actions';
                const canReorder = reorderable && col.reorderable !== false;

                return (
                  <th
                    key={col.key}
                    draggable={canReorder}
                    onDragStart={e => {
                      if (!canReorder) return;
                      draggedColumn.current = col.key;
                      e.dataTransfer.effectAllowed = 'move';
                      e.dataTransfer.setData('text/plain', col.key);
                    }}
                    onDragOver={e => {
                      if (!draggedColumn.current) return;
                      e.preventDefault();
                      setDragOverColumn(col.key);
                    }}
                    onDrop={e => {
                      e.preventDefault();
                      if (draggedColumn.current) reorderColumns(draggedColumn.current, col.key);
                      draggedColumn.current = null;
                      setDragOverColumn(null);
                    }}
                    onDragEnd={() => { draggedColumn.current = null; setDragOverColumn(null); }}
                    className={`relative px-4 py-3 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider border-r border-gray-200 dark:border-gray-700 ${dragOverColumn === col.key ? 'bg-blue-100 dark:bg-blue-900/40' : ''}`}
                    style={{ textAlign: col.align ?? 'left' }}
                  >
                    <button
                      type="button"
                      onClick={() => handleSort(col)}
                      disabled={!col.sortable}
                      className={`flex items-center gap-1 w-full ${col.align === 'right' ? 'justify-end' : col.align === 'center' ? 'justify-center' : ''} ${col.sortable ? 'hover:text-blue-600' : 'cursor-default'}`}
                    >
                      <span className="truncate">{col.header}</span>
                      {col.sortable && (
                        <span className={`text-[10px] ${sortColumn === col.key ? 'text-blue-600' : 'text-gray-300 dark:text-gray-600'}`}>
                          {sortColumn === col.key ? (sortDirection === 'asc' ? '↑' : '↓') : '↕'}
                        </span>
                      )}
                    </button>

                    {canResize && (
                      <span
                        onPointerDown={e => startResize(e, col)}
                        onPointerMove={moveResize}
                        onPointerUp={endResize}
                        onPointerCancel={endResize}
                        onClick={e => e.stopPropagation()}
                        title="Drag to resize"
                        className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-blue-400/60 touch-none z-10"
                      />
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
            {sortedData.length === 0 && (
              <tr>
                <td colSpan={visibleColumns.length + (selectable ? 1 : 0)} className="p-12 text-center">
                  <div className="text-4xl mb-3">{emptyState?.icon ?? '📭'}</div>
                  <p className="font-medium text-gray-700 dark:text-gray-200">{emptyState?.title ?? emptyMessage}</p>
                  {emptyState?.description && <p className="text-sm text-gray-500 mt-1">{emptyState.description}</p>}
                  {emptyState?.action && (
                    <button onClick={emptyState.action.onClick} className="mt-4 px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700">
                      {emptyState.action.label}
                    </button>
                  )}
                </td>
              </tr>
            )}

            {sortedData.map(row => {
              const isSelected = selectedIds.includes(row.id);
              const isExpanded = expandedRowId === row.id;

              return (
                <Fragment key={row.id}>
                  <tr
                    draggable={!!onDragStart}
                    onDragStart={e => onDragStart?.(row.id, e)}
                    onDragOver={e => { e.preventDefault(); onDragOver?.(e); }}
                    onDrop={e => { e.preventDefault(); onDrop?.(row.id, e); }}
                    onClick={() => { if (onRowExpand && !onDragStart) onRowExpand(isExpanded ? null : row.id); }}
                    className={`transition-colors ${onDragStart ? 'cursor-grab active:cursor-grabbing' : ''} ${onRowExpand ? 'cursor-pointer' : ''} ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'}`}
                  >
                    {selectable && (
                      <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={e => setSelectedIds(prev => e.target.checked ? [...prev, row.id] : prev.filter(id => id !== row.id))}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                      </td>
                    )}

                    {visibleColumns.map(col => {
                      const isEditing = editing?.rowId === row.id && editing?.key === col.key;
                      const canEdit = inlineEdit && col.editable && col.type !== 'actions';
                      const content = col.render
                        ? col.render(row)
                        : (row[(col.accessor ?? col.key) as keyof T] as ReactNode) || '—';

                      return (
                        <td
                          key={col.key}
                          className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 align-top"
                          style={{ textAlign: col.align ?? 'left' }}
                          onClick={e => { if (canEdit) e.stopPropagation(); }}
                        >
                          {isEditing ? (
                            <div onClick={e => e.stopPropagation()}>
                              <div className="flex gap-1">
                                <input
                                  autoFocus
                                  value={draft}
                                  disabled={saving}
                                  placeholder={col.placeholder}
                                  onChange={e => { setDraft(e.target.value); setEditError(null); }}
                                  onKeyDown={e => {
                                    if (e.key === 'Enter') { e.preventDefault(); commitEdit(); }
                                    if (e.key === 'Escape') { e.preventDefault(); setEditing(null); }
                                  }}
                                  className={`w-full min-w-0 px-2 py-1 text-sm border rounded bg-white dark:bg-gray-900 outline-none ${editError ? 'border-red-500' : 'border-blue-400'}`}
                                />
                                <button onClick={commitEdit} disabled={saving} className="px-2 text-green-600 text-sm flex items-center" title="Save">
                                  <Icon name="Check" size={16} />
                                </button>
                                <button onClick={() => setEditing(null)} className="px-1 text-gray-400 text-sm flex items-center" title="Cancel">
                                  <Icon name="X" size={16} />
                                </button>
                              </div>
                              {editError && <p className="mt-1 text-xs text-red-600">{editError}</p>}
                            </div>
                          ) : canEdit ? (
                            <button
                              onClick={() => startEdit(row, col)}
                              className="group flex w-full items-center gap-2 text-left hover:text-blue-600"
                              title="Click to edit"
                            >
                              <span className="truncate">{content}</span>
                              <span className="opacity-0 group-hover:opacity-100 text-gray-400 text-xs flex items-center">
                                <Icon name="Pencil" size={14} />
                              </span>
                            </button>
                          ) : (
                            <div className={col.type === 'actions' ? '' : 'truncate'}>{content}</div>
                          )}
                        </td>
                      );
                    })}
                  </tr>

                  {isExpanded && expandedRowRender && (
                    <tr className="bg-gray-50 dark:bg-gray-900/50">
                      <td colSpan={visibleColumns.length + (selectable ? 1 : 0)} className="p-4">
                        {expandedRowRender(row)}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {meta && onPageChange && <Pagination meta={meta} onPageChange={onPageChange} />}
    </div>
  );
}

function Pagination({ meta, onPageChange }: { meta: PaginationMeta; onPageChange: (page: number) => void }) {
  const { page, totalPages, total, pageSize } = meta;
  const [gotoPage, setGotoPage] = useState(page.toString());

  useEffect(() => { setGotoPage(page.toString()); }, [page]);

  function go(value: string) {
    const n = Number(value);
    if (n >= 1 && n <= totalPages && n !== page) onPageChange(n);
    else setGotoPage(page.toString());
  }

  const btn = 'px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed';

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-3 border-t border-gray-200 dark:border-gray-700">
      <div className="text-sm text-gray-600 dark:text-gray-400">
        Showing <b>{(page - 1) * pageSize + 1}</b>–<b>{Math.min(page * pageSize, total)}</b> of <b>{total}</b>
      </div>
      <div className="flex items-center gap-1">
        <button className={btn} disabled={page <= 1} onClick={() => onPageChange(1)}>«</button>
        <button className={btn} disabled={page <= 1} onClick={() => onPageChange(page - 1)}>‹</button>
        <span className="px-2 text-sm">Page</span>
        <input
          type="number" min={1} max={totalPages} value={gotoPage}
          onChange={e => setGotoPage(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') go(gotoPage); }}
          onBlur={() => go(gotoPage)}
          className="w-16 px-2 py-1 text-center text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800"
        />
        <span className="px-2 text-sm">of {totalPages}</span>
        <button className={btn} disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>›</button>
        <button className={btn} disabled={page >= totalPages} onClick={() => onPageChange(totalPages)}>»</button>
      </div>
    </div>
  );
}
